import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  InboxIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { REQUIREMENT_FILE_TYPES } from '../constants';

function FileMenu({ file, categoryKey, handleDownloadFile, handleDeleteFile }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 80;
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuPos({
        top: spaceBelow < menuHeight ? rect.top - menuHeight : rect.bottom + 4,
        left: rect.right - 120,
      });
    }
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    if (menuRef.current) menuRef.current.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div>
      <button
        ref={btnRef}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
      >
        <EllipsisVerticalIcon className="w-4 h-4 text-neutral-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            className="fixed z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[120px]"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); handleDownloadFile(file.name, categoryKey, file.folder_path); setOpen(false); }}
              className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
            <button
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); handleDeleteFile(file.name, file.folder_path, categoryKey); setOpen(false); }}
              className="w-full px-3 py-1.5 text-left text-sm text-danger-600 dark:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function parseFileName(name) {
  if (!name) return { base: 'Unknown file', timestamp: '', ext: '' };
  const dotIdx = name.lastIndexOf('.');
  const ext = dotIdx > 0 ? name.slice(dotIdx) : '';
  const nameWithoutExt = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  const tsMatch = nameWithoutExt.match(/_(\d{8}_\d{6})$/);
  if (tsMatch) {
    const base = nameWithoutExt.slice(0, tsMatch.index) || name;
    const raw = tsMatch[1];
    const formatted = `${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)} ${raw.slice(9,11)}:${raw.slice(11,13)}`;
    return { base, timestamp: formatted, ext };
  }
  return { base: nameWithoutExt, timestamp: '', ext };
}

function FileName({ name, selected }) {
  const { base, timestamp, ext } = parseFileName(name);
  const displayBase = base.replace(/_/g, ' ');

  return (
    <span className="file-name-display" title={name}>
      <span className={`file-name-base ${selected ? 'text-primary-900 dark:text-primary-100 font-medium' : 'text-neutral-800 dark:text-neutral-200'}`}>
        {displayBase}
      </span>
      {ext && (
        <span className="file-name-ext text-neutral-400 dark:text-neutral-500 font-mono text-xs ml-0.5">
          {ext}
        </span>
      )}
      {timestamp && (
        <span className="file-name-ts text-neutral-400 dark:text-neutral-500 text-xs block mt-0.5 font-mono tabular-nums">
          {timestamp}
        </span>
      )}
    </span>
  );
}

function SelectableList({
  categoryKey,
  files,
  getCategoryInfo,
  expandedCategories,
  toggleCategory,
  handleSelectFileForProcessing,
  handleDownloadFile,
  handleDeleteFile,
  selectedRequirementFile,
  selectedRulesFile,
  highlightedReport,
  clearHighlight,
  searchQuery
}) {
  const categoryInfo = getCategoryInfo(categoryKey);
  const isExpanded = expandedCategories[categoryInfo.name];
  const isSelectable = categoryKey === 'requirements' || categoryKey === 'rules';
  const hasMounted = useRef(false);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const isSelected = (file) => {
    if (categoryKey === 'requirements') return selectedRequirementFile?.name === file.name;
    if (categoryKey === 'rules') return selectedRulesFile?.name === file.name;
    return false;
  };

  const filteredFiles = useMemo(() => {
    if (!searchQuery) return files;
    const q = searchQuery.toLowerCase();
    return files.filter(f => {
      if (!f.name) return false;
      const raw = f.name.toLowerCase();
      const display = raw.replace(/_/g, ' ');
      return raw.includes(q) || display.includes(q);
    });
  }, [files, searchQuery]);

  useEffect(() => {
    if (categoryKey === 'reports' && highlightedReport && searchQuery) {
      const stillVisible = filteredFiles.some(f => f.name && f.name.includes(highlightedReport));
      if (!stillVisible && clearHighlight) clearHighlight();
    }
  }, [categoryKey, highlightedReport, searchQuery, filteredFiles, clearHighlight]);

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
      <button
        onClick={() => toggleCategory(categoryInfo.name)}
        className="w-full px-3 py-2 bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{categoryInfo.icon}</span>
          <span className="text-sm font-medium text-neutral-900 dark:text-white">
            {categoryInfo.name}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            ({filteredFiles.length}{searchQuery && filteredFiles.length !== files.length ? `/${files.length}` : ''})
          </span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
        </motion.div>
      </button>

      {categoryKey === 'rules' && (
        <div className="px-3 py-1.5 text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-700 flex flex-col items-start gap-1.5">
          <span>Optional. If no rules file is selected, default rules will be used.</span>
          <button
            type="button"
            onClick={() => handleDownloadFile('default_rules.zip', 'rules', null, true)}
            className={`w-full inline-flex justify-center items-center rounded-md px-2 py-0.5 text-xs font-medium transition-colors
                ${!selectedRulesFile
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-800 dark:text-neutral-100'}`}
          >
            Download Default Rules
          </button>
        </div>
      )}

      {categoryKey === 'reports' && (
        <p className="px-3 py-1 text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-700">
          * Recommended to download and store reports for backup
        </p>
      )}

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="max-h-72 overflow-y-auto filelist-scroll">
              {filteredFiles.length === 0 && searchQuery && (
                <div className="px-3 py-4 text-center text-xs text-neutral-400 dark:text-neutral-500">
                  No files match "{searchQuery}"
                </div>
              )}
              {filteredFiles.map((file, idx) => {
                const selected = isSelected(file);
                const isHighlighted = categoryKey === 'reports' && highlightedReport && file.name.includes(highlightedReport);

                return (
                  <motion.div
                    key={file.name}
                    initial={hasMounted.current ? false : { opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15, delay: hasMounted.current ? 0 : Math.min(idx * 0.02, 0.3) }}
                    onClick={() => isSelectable && handleSelectFileForProcessing(file, categoryKey)}
                    onMouseEnter={() => isHighlighted && clearHighlight && clearHighlight()}
                    className={`flex items-start justify-between px-3 py-2 border-t border-neutral-100 dark:border-neutral-800 border-l-2 transition-colors ${
                      isSelectable ? 'cursor-pointer' : ''
                    } ${
                      selected
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-l-primary-500'
                        : isHighlighted
                          ? 'bg-success-100 dark:bg-success-900/50 border-l-transparent'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/30 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      {isSelectable && (
                        <CheckCircleIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 transition-colors ${
                          selected
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-neutral-300 dark:text-neutral-600'
                        }`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <FileName name={file.name} selected={selected} />
                        {categoryKey === 'requirements' && file.file_standard && (
                          <span className="inline-block mt-1 text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
                            {REQUIREMENT_FILE_TYPES[file.file_standard]?.label || file.file_standard}
                          </span>
                        )}
                      </div>
                    </div>
                    <FileMenu
                      file={file}
                      categoryKey={categoryKey}
                      handleDownloadFile={handleDownloadFile}
                      handleDeleteFile={handleDeleteFile}
                    />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FileList({
  fileList,
  isLoadingFiles,
  hasAnyFiles,
  expandedCategories,
  toggleCategory,
  handleSelectFileForProcessing,
  handleDownloadFile,
  handleDeleteFile,
  selectedRequirementFile,
  selectedRulesFile,
  getCategoryInfo,
  highlightedReport,
  clearHighlight
}) {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div>
      {isLoadingFiles ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-primary-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-neutral-600 dark:text-neutral-400 text-sm">Loading files...</span>
          </div>
        </div>
      ) : !hasAnyFiles ? (
        <div className="text-center py-8">
          <InboxIcon className="w-8 h-8 text-neutral-400 dark:text-neutral-500 mx-auto mb-2" />
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No files found
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
            Upload your first file to get started
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Search / Filter */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter files..."
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800/60 text-neutral-800 dark:text-neutral-200 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5 text-neutral-400" />
              </button>
            )}
          </div>

          {/* Requirements and Rules stacked for full width */}
          <div className="space-y-2">
            {['requirements', 'rules'].map((categoryKey) => {
              const files = fileList[categoryKey];
              if (!Array.isArray(files)) return null;
              return (
                <SelectableList
                  key={categoryKey}
                  categoryKey={categoryKey}
                  files={files}
                  getCategoryInfo={getCategoryInfo}
                  expandedCategories={expandedCategories}
                  toggleCategory={toggleCategory}
                  handleSelectFileForProcessing={handleSelectFileForProcessing}
                  handleDownloadFile={handleDownloadFile}
                  handleDeleteFile={handleDeleteFile}
                  selectedRequirementFile={selectedRequirementFile}
                  selectedRulesFile={selectedRulesFile}
                  highlightedReport={highlightedReport}
                  clearHighlight={clearHighlight}
                  searchQuery={searchQuery}
                />
              );
            })}
          </div>

          {/* Reports below */}
          {fileList.reports && Array.isArray(fileList.reports) && fileList.reports.length > 0 && (
            <SelectableList
              categoryKey="reports"
              files={fileList.reports}
              getCategoryInfo={getCategoryInfo}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              handleSelectFileForProcessing={handleSelectFileForProcessing}
              handleDownloadFile={handleDownloadFile}
              handleDeleteFile={handleDeleteFile}
              selectedRequirementFile={selectedRequirementFile}
              selectedRulesFile={selectedRulesFile}
              highlightedReport={highlightedReport}
              clearHighlight={clearHighlight}
              searchQuery={searchQuery}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default FileList;
